import { Component, OnInit, DestroyRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { AVAILABLE_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MonEventTriggerModule } from '@monsido/angular-shared-components';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { ParamService } from '@monsido/core/param/param.service';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuModule } from 'primeng/menu';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { LabelsRepo } from '@client/app/feature-modules/shared/label/services/labels.repo';
import { Label } from '@client/app/feature-modules/shared/label/types/label.type';
import { AdaptiveColorPipe } from '@client/app/feature-modules/admin/modules/admin-labels/pipes/adaptive-color.pipe';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { LabelDialogService } from '@client/app/feature-modules/shared/label/services/label-dialog.service';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { StateService } from '@uirouter/core';
import { TableWrapperComponent } from '@client/app/shared-modules/table/components/table-wrapper.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';

export type ComputedLabel = Omit<Label, 'created_at' | 'updated_at'> & {
    actionItems: MenuItem[];
};

@Component({
    selector: 'mon-admin-labels',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        TranslocoModule,
        MonEventTriggerModule,
        ConfirmDialog,
        MenuModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        AdaptiveColorPipe,
        PageLayoutComponent,
        TableWrapperComponent,
        TableCellTemplateDirective,
    ],
    providers: [
        ConfirmationService,
        DialogService,
        LabelDialogService,
    ],
    templateUrl: './admin-labels.component.html',
    styleUrls: ['./admin-labels.component.scss'],
})
export class AdminLabelsComponent implements OnInit {
    private readonly translocoService = inject(TranslocoService);

    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;

    readonly TABLE_SCHEMA = TableWrapperComponent.createTableSchema<ComputedLabel>({
        LABEL: {
            label: this.translocoService.translate('admin.labels.table.label'),
            id: 'label',
            type: 'custom',
            widthClass: 'tw-w-auto',
        },
        ACTIONS: {
            label: this.translocoService.translate('admin.labels.table.actions'),
            id: 'actions',
            type: 'custom',
            widthClass: 'tw-w-20',
            actionColumn: true,
        },
    });

    @ViewChild(TableWrapperComponent) private readonly tableWrapper?: TableWrapperComponent<ComputedLabel>;

    private menuTriggerElement: HTMLElement | null = null;

    labels: ComputedLabel[] = [];
    currentPage = 1;
    perPage = AVAILABLE_PAGE_SIZE[0];
    total = 0;
    paginationTotal = 0;
    searchText = '';
    loading = true;
    loadingTotal = true;
    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor (
        private readonly labelsRepo: LabelsRepo,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly paramService: ParamService,
        private readonly confirmationService: ConfirmationService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly destroyRef: DestroyRef,
        private readonly adminLayoutService: AdminLayoutService,
        private readonly labelDialogService: LabelDialogService,
        private readonly stateService: StateService,
    ) {
        this.searchSubject.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((searchText) => {
            this.searchLabels(searchText);
        });
    }

    ngOnInit (): void {
        this.setBreadcrumbs();

        const { page_size, page, search } = this.paramService.getParams();
        this.searchText = search || '';

        this.getTotalLabels();
        this.getLabels(parseInt(page), parseInt(page_size), search).then();

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((params) => {
            this.getLabels(parseInt(params.page), parseInt(params.page_size), params.search).then();
        });

        this.destroyRef.onDestroy(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            deregisterHook();
            this.destroy$.next();
            this.destroy$.complete();
        });
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.labels.table.breadcrumbLabel'),
                    state: 'base.customer.admin.labels',
                },
            ],
        });
    }

    async getLabels (page: number, pageSize: number, search: string): Promise<void> {
        this.loading = true;
        try {
            const labelsResponse = await this.labelsRepo.getLabels({
                page: page,
                page_size: pageSize,
                search,
            });

            if (!labelsResponse) {
                return;
            }

            this.perPage = labelsResponse.perPage || 0;
            this.currentPage = labelsResponse.currentPage || 1;
            this.paginationTotal = labelsResponse.total || 0;
            this.labels = labelsResponse.map(label => {
                const computedLabel: ComputedLabel = {
                    id: label.id,
                    name: label.name,
                    color: label.color,
                    actionItems: [
                        {
                            label: this.translocoService.translate('admin.labels.actions.editButtonLabel'),
                            icon: 'pi pi-pencil',
                            command: () => this.addEditLabel(computedLabel),
                        },
                        {
                            label: this.translocoService.translate('admin.labels.actions.deleteButtonLabel'),
                            icon: 'pi pi-trash',
                            command: () => this.deleteLabel(computedLabel),
                        },
                    ] as MenuItem[],
                };
                return computedLabel;
            });
        } finally {
            this.loading = false;
        }
    }

    async getTotalLabels (): Promise<void> {
        this.loadingTotal = true;
        try {
            const labelsResponse = await this.labelsRepo.getLabels({
                page: 1,
                page_size: 1,
            });
            this.total = labelsResponse?.total || 0;
        } finally {
            this.loadingTotal = false;
        }
    }

    async reload (): Promise<void> {
        const { page_size, page, search } = this.paramService.getParams();
        this.getTotalLabels();
        return this.getLabels(parseInt(page), parseInt(page_size), search);
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.paramService.updateParams({
            page: pageNumber,
            page_size: rows,
            search: this.searchText,
        });
    }

    onSearchInput (event: Event) {
        const searchValue = (event.target as HTMLInputElement).value;
        this.searchSubject.next(searchValue);
    }

    private searchLabels (searchText: string) {
        this.searchText = searchText;
        this.paramService.updateParams({
            page: 1,
            search: this.searchText,
        });
    }

    addEditLabel (selectedLabel?: ComputedLabel): void {
        const isEdit = !!selectedLabel?.id;

        if (!isEdit) {
            this.actionTriggerService.track({ action: 'admin-labels-add-label-btn' });
        } else {
            this.actionTriggerService.track({ action: 'admin-labels-edit-label-btn' });
        }

        const dialogRef = this.labelDialogService.openLabelDialog({
            model: isEdit ? { labelName: selectedLabel.name, colorCode: selectedLabel.color } : null,
            header: {
                text: isEdit
                    ? this.translocoService.translate('admin.labels.dialogHeader.edit')
                    : this.translocoService.translate('admin.labels.dialogHeader.add'),
            },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dialogRef.onClose.subscribe(async (result: any | null) => {
            if (!result) {
                return;
            }

            this.loading = true;
            const label = isEdit
                ? await this.labelsRepo.updateLabel({
                    id: selectedLabel.id,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    name: result.labelName,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    color: result.colorCode,
                })
                : await this.labelsRepo.createLabel({
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    name: result.labelName,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    color: result.colorCode,
                });

            if (!label) {
                return;
            }

            this.globalToastMessageService.success(
                isEdit
                    ? this.translocoService.translate('admin.labels.toastDetail.labelUpdated')
                    : this.translocoService.translate('admin.labels.toastDetail.labelCreated'),
            );
            await this.reload();
        });
    }

    public onMenuHide (): void {
        const el = document.activeElement;
        this.menuTriggerElement = (el instanceof HTMLElement) ? el : null;
    }

    deleteLabel (selectedLabel: ComputedLabel): void {
        this.tableWrapper?.trackAction(this.labels, selectedLabel, 'delete', () => true);
        this.actionTriggerService.track({ action: 'admin-labels-delete-label-btn' });
        this.confirmationService.confirm({
            message: this.translocoService.translate('admin.labels.deleteDialog.message{{labelName}}', { labelName: selectedLabel.name }),
            header: this.translocoService.translate('admin.labels.deleteDialog.header'),
            closable: true,
            closeOnEscape: true,
            dismissableMask: true,
            rejectButtonProps: {
                label: this.translocoService.translate('admin.labels.deleteDialog.cancelBtnLabel'),
                severity: 'secondary',
                variant: 'text',
            },
            acceptButtonProps: {
                label: this.translocoService.translate('admin.labels.deleteDialog.deleteBtnLabel'),
                severity: 'danger',
            },
            reject: () => {
                this.tableWrapper?.focusService?.reset();
                this.menuTriggerElement?.focus();
                this.menuTriggerElement = null;
            },
            accept: async () => {
                this.loading = true;
                try {
                    await this.labelsRepo.deleteLabel({ id: selectedLabel.id });
                    this.globalToastMessageService.info(
                        this.translocoService.translate('admin.labels.toastDetail.labelDeleted{{labelName}}', {
                            labelName: selectedLabel.name,
                        }),
                    );
                    await this.reload();
                } catch (err) {
                    this.onDeleteLabelError(err, selectedLabel);
                } finally {
                    this.loading = false;
                }
            },
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private onDeleteLabelError (reason: any, selectedLabel: ComputedLabel): void {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (reason?.errors) {
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.labels.toastDetail.labelDeleteError{{labelName}}', {
                    labelName: selectedLabel.name,
                }),
            );
        } else {
            const ref = this.labelDialogService.openLabelInUseDialogSimple(selectedLabel);
            ref.onClose.subscribe(result => {
                if (result?.goToOverview) {
                    this.stateService.go('base.customer.dashboard');
                }
            });
        }
    }
}
